import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  DatePicker,
  Input,
  Modal,
  Pagination,
  Select,
  Table,
  Tabs,
  message,
} from 'antd';
import { Button as CrceButton, Spinner } from '@crc-evans/ui-components';
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  Filter,
  History,
  RefreshCw,
  Search,
  Shield,
} from 'lucide-react';
import moment from 'moment';
import get from 'lodash/get';
import axiosConfig from '../../services/axiosConfig';
import {
  exportAuditTrailCsv,
  fetchAuditInsights,
  fetchAuditTrail,
  fetchAuditTrailDashboardStats,
  fetchEntityAuditHistory,
  fetchUserActivityTimeline,
} from '../../services/auditTrailApi';
import {
  logActivityExport,
  logActivityFilterApplied,
  logActivityFilterCleared,
  logActivityPageView,
  logActivitySearch,
} from '../../features/audit/activityAudit';
import '../../pages/technical/HeatProfilePage.css';
import './AuditTrailView.css';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Option } = Select;

const MODULES = [
  'ACTIVITY',
  'COGNITO',
  'PROJECT',
  'EQUIPMENT',
  'MAINTENANCE',
  'DSR',
  'DPR',
  'PQT',
  'SPARES',
  'MATERIALS',
  'EMPLOYEE',
  'SHIPPING',
  'REPORTS',
  'SECURITY',
  'SYSTEM',
];

const popupToBody = () => document.body;

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

/** e.g. 24 Sept 2026, 17:16:16 — matches Data360 audit trail */
function formatWhen(iso) {
  if (!iso) return '—';
  const m = moment(iso);
  if (!m.isValid()) return String(iso);
  return `${m.date()} ${MONTH_SHORT[m.month()]} ${m.year()}, ${m.format('HH:mm:ss')}`;
}

function severityClass(sev) {
  const s = String(sev || 'INFO').toUpperCase();
  if (s === 'ERR' || s === 'ERROR') return 'audit-sev--err';
  if (s === 'WARN' || s === 'WARNING') return 'audit-sev--warn';
  return 'audit-sev--info';
}

/** e.g. 25 Sept 2026 · 09:55 - 09:55 */
function formatSessionRange(session) {
  const start = session?.started_at || session?.start_at || session?.events?.[0]?.created_at;
  const end =
    session?.ended_at ||
    session?.end_at ||
    session?.events?.[(session?.events?.length || 1) - 1]?.created_at ||
    start;
  const s = moment(start);
  const e = moment(end);
  if (!s.isValid()) return '—';
  const datePart = `${s.date()} ${MONTH_SHORT[s.month()]} ${s.year()}`;
  const startT = s.format('HH:mm');
  const endT = e.isValid() ? e.format('HH:mm') : startT;
  return `${datePart} · ${startT} - ${endT}`;
}

function formatClock(iso) {
  const m = moment(iso);
  return m.isValid() ? m.format('HH:mm:ss') : '—';
}

function shortSessionId(id) {
  if (!id) return '—';
  const s = String(id);
  if (s.length <= 10) return s;
  return s.slice(0, 8);
}

function eventHeadline(row) {
  return row?.summary || row?.display_title || row?.event_label || row?.event_type || '—';
}

function eventMetaLine(row) {
  const ent = resolveEntityDisplay(row);
  const parts = [
    row?.event_label || row?.event_type,
    row?.actor_email || row?.actor_name || row?.actor_id,
    ent.primary !== '—' ? ent.primary : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

function eventTypeLabel(row) {
  const raw = row?.event_type || row?.event_label || '';
  return String(raw)
    .replace(/^ACTIVITY_/, '')
    .replace(/_/g, ' ')
    .trim()
    .toUpperCase() || 'EVENT';
}

/** Prefer BE entity_context readable fields over raw ids. */
function resolveEntityDisplay(row) {
  const ctx = row?.entity_context && typeof row.entity_context === 'object' ? row.entity_context : null;
  const displayName =
    row?.entity_name ||
    ctx?.displayName ||
    ctx?.name ||
    ctx?.title ||
    null;
  const serial = ctx?.serial || ctx?.serialNumber || ctx?.serial_number || null;
  const category = ctx?.category || ctx?.categoryName || ctx?.equipmentCategory || null;
  const primary = displayName || (row?.entity_id != null ? String(row.entity_id) : null);
  const bits = [serial, category].filter(Boolean);
  return {
    primary: primary || '—',
    secondary: bits.length ? bits.join(' · ') : null,
    type: row?.entity_type || null,
    hasEntity: !!(row?.entity_type || row?.entity_id != null || displayName),
  };
}

/** Format changed_fields / JSON values for display (arrays of named objects → name lists). */
function formatAuditValue(v) {
  if (v == null) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v || '—';
  if (Array.isArray(v)) {
    if (!v.length) return '[]';
    const names = v
      .map((item) => {
        if (item == null) return null;
        if (typeof item !== 'object') return String(item);
        return (
          item.name ||
          item.displayName ||
          item.label ||
          item.title ||
          item.typeName ||
          item.maintenanceTypeName ||
          item.categoryName ||
          null
        );
      })
      .filter(Boolean);
    if (names.length === v.length) return names.join(', ');
    if (names.length) return `${names.join(', ')} (+${v.length - names.length} more)`;
    try {
      return JSON.stringify(v);
    } catch {
      return `[${v.length} items]`;
    }
  }
  if (typeof v === 'object') {
    const named =
      v.name || v.displayName || v.label || v.title || v.typeName || v.maintenanceTypeName;
    if (named && Object.keys(v).length <= 4) {
      return String(named);
    }
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function humanizeContextKey(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Shared Event log / User activity / Entity history dashboard.
 * Admin: enableProjectFilter. Project: pass projectId (+ optional projectName).
 */
export default function AuditTrailView({
  projectId: fixedProjectId,
  projectName: fixedProjectName,
  enableProjectFilter = false,
  description = 'Activity log for compliance and troubleshooting.',
}) {
  const [viewMode, setViewMode] = useState('log');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [module, setModule] = useState(undefined);
  const [severity, setSeverity] = useState(undefined);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [projectFilter, setProjectFilter] = useState(undefined);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  /** all | be | ui — default API changes */
  const [eventSource, setEventSource] = useState('be');
  const [stats, setStats] = useState(null);
  const [detail, setDetail] = useState(null);
  const [actionDetailsOpen, setActionDetailsOpen] = useState(false);

  const [actorId, setActorId] = useState('');
  const [actorQuery, setActorQuery] = useState('');
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [activityFiltersOpen, setActivityFiltersOpen] = useState(false);

  const [entityType, setEntityType] = useState('');
  const [entityId, setEntityId] = useState('');
  const [entitySearch, setEntitySearch] = useState('');
  const [entityHistory, setEntityHistory] = useState(null);
  const [entityLoading, setEntityLoading] = useState(false);
  const [entityFiltersOpen, setEntityFiltersOpen] = useState(false);
  const [entityBrowseItems, setEntityBrowseItems] = useState([]);
  const [entityBrowseTotal, setEntityBrowseTotal] = useState(0);

  const resolvedProjectId = enableProjectFilter
    ? projectFilter != null && projectFilter !== ''
      ? Number(projectFilter)
      : undefined
    : fixedProjectId != null
      ? Number(fixedProjectId)
      : undefined;

  const resolvedProjectName = enableProjectFilter
    ? projectOptions.find((p) => Number(p.id) === Number(projectFilter))?.projectName ||
      projectOptions.find((p) => Number(p.id) === Number(projectFilter))?.name
    : fixedProjectName;

  useEffect(() => {
    if (!enableProjectFilter) return undefined;
    let cancelled = false;
    (async () => {
      setProjectsLoading(true);
      try {
        const res = await axiosConfig.get('/project');
        const list = get(res, ['data', 'data'], get(res, ['data'], []));
        const rows = Array.isArray(list) ? list : [];
        if (!cancelled) {
          setProjectOptions(
            rows
              .map((p) => ({
                id: p.id,
                projectName: p.projectName || p.name || '',
                projectId: p.projectId || '',
              }))
              .filter((p) => p.id != null)
              .sort((a, b) =>
                String(a.projectName).localeCompare(String(b.projectName), undefined, {
                  sensitivity: 'base',
                })
              )
          );
        }
      } catch {
        if (!cancelled) setProjectOptions([]);
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enableProjectFilter]);

  useEffect(() => {
    logActivityPageView(enableProjectFilter ? 'Admin Audit Trail' : 'Project Audit Trail', {
      projectId: resolvedProjectId,
      projectName: resolvedProjectName,
    });
  }, [enableProjectFilter, resolvedProjectId, resolvedProjectName]);

  const queryParams = useMemo(() => {
    const params = {
      limit: pageSize,
      offset: (page - 1) * pageSize,
      sortBy: 'created_at',
      sortOrder: 'DESC',
    };
    if (resolvedProjectId != null && Number.isFinite(resolvedProjectId)) {
      params.project_id = resolvedProjectId;
    }
    if (module) params.module = module;
    if (severity) params.severity = severity;
    if (eventSource && eventSource !== 'all') params.source = eventSource;
    if (search.trim()) params.search = search.trim();
    if (dateRange?.[0]) params.created_from = dateRange[0].clone().startOf('day').toISOString();
    if (dateRange?.[1]) params.created_to = dateRange[1].clone().endOf('day').toISOString();
    return params;
  }, [page, pageSize, resolvedProjectId, module, severity, search, dateRange, eventSource]);

  const loadLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [listRes, statsRes, insightsRes] = await Promise.all([
      fetchAuditTrail(queryParams),
      fetchAuditTrailDashboardStats(queryParams),
      fetchAuditInsights('summary', queryParams),
    ]);
    setLoading(false);
    if (!listRes.ok) {
      setError(listRes.error || 'Failed to load');
      setItems([]);
      setTotal(0);
      return;
    }
    setItems(listRes.data.items || []);
    setTotal(listRes.data.total || 0);
    if (statsRes.ok) setStats(statsRes.data);
    else if (insightsRes.ok) {
      setStats({
        total_events: insightsRes.data?.kpis?.total_events,
        events_today: insightsRes.data?.kpis?.events_today,
        failure_events: insightsRes.data?.kpis?.failure_events,
        security_events: insightsRes.data?.kpis?.security_events,
      });
    }
  }, [queryParams]);

  useEffect(() => {
    if (viewMode === 'log') loadLog();
  }, [viewMode, loadLog]);

  const applyFilters = () => {
    setPage(1);
    logActivityFilterApplied(
      'audit-trail',
      { module, severity, search, projectId: resolvedProjectId, projectName: resolvedProjectName, source: eventSource },
      resolvedProjectId,
      resolvedProjectName
    );
    if (search.trim()) {
      logActivitySearch('audit-trail', search, resolvedProjectId, resolvedProjectName);
    }
  };

  const clearFilters = () => {
    setModule(undefined);
    setSeverity(undefined);
    setSearch('');
    setDateRange(null);
    setProjectFilter(undefined);
    setEventSource('be');
    setPage(1);
    logActivityFilterCleared('audit-trail', resolvedProjectId, resolvedProjectName);
  };

  const handleExport = async () => {
    try {
      const blob = await exportAuditTrailCsv(queryParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pih-audit-trail-${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      logActivityExport('audit-trail-csv', {
        projectId: resolvedProjectId,
        projectName: resolvedProjectName,
      });
      message.success('Export started');
    } catch (e) {
      message.error(e?.message || 'Export failed');
    }
  };

  const loadTimeline = async (actorOverride) => {
    const actor = String(actorOverride ?? actorId).trim();
    setTimelineLoading(true);
    const res = await fetchUserActivityTimeline({
      ...(actor ? { actor_id: actor } : {}),
      project_id: resolvedProjectId,
      limit: 100,
    });
    setTimelineLoading(false);
    if (!res.ok) {
      message.error(res.error);
      setTimeline(null);
      setSelectedSessionId(null);
      return;
    }
    setTimeline(res.data);
    const firstId = res.data?.sessions?.[0]?.session_id ?? null;
    setSelectedSessionId(firstId);
  };

  const normalizeEntityHistoryList = (data) => {
    if (!data) return [];
    const list = data.history || data.items || data.audit_trail || data.events || [];
    return Array.isArray(list) ? list : [];
  };

  const loadEntityBrowse = useCallback(
    async (searchOverride) => {
      setEntityLoading(true);
      const q = String(searchOverride ?? entitySearch).trim();
      const res = await fetchAuditTrail({
        limit: 100,
        offset: 0,
        sortBy: 'created_at',
        sortOrder: 'DESC',
        project_id: resolvedProjectId,
        ...(q ? { search: q } : {}),
      });
      setEntityLoading(false);
      if (!res.ok) {
        message.error(res.error || 'Failed to load history');
        setEntityBrowseItems([]);
        setEntityBrowseTotal(0);
        return;
      }
      setEntityHistory(null);
      setEntityBrowseItems(res.data.items || []);
      setEntityBrowseTotal(res.data.total || 0);
    },
    [entitySearch, resolvedProjectId]
  );

  const loadEntity = async (typeOverride, idOverride) => {
    const type = String(typeOverride ?? entityType).trim();
    const id = String(idOverride ?? entityId).trim();
    if (!type || !id) {
      message.warning('Entity type and id are required');
      return;
    }
    setEntityType(type);
    setEntityId(id);
    setEntityLoading(true);
    const res = await fetchEntityAuditHistory({
      entity_type: type,
      entity_id: id,
      project_id: resolvedProjectId,
      limit: 100,
    });
    setEntityLoading(false);
    if (!res.ok) {
      message.error(res.error);
      setEntityHistory({ history: [] });
      setEntityBrowseItems([]);
      setEntityBrowseTotal(0);
      return;
    }
    const list = normalizeEntityHistoryList(res.data);
    setEntityHistory({ ...(res.data || {}), history: list });
    setEntityBrowseItems([]);
    setEntityBrowseTotal(list.length);
  };

  const openEntityHistoryFromDetail = () => {
    if (!detail?.entity_type || detail?.entity_id == null || detail?.entity_id === '') {
      message.warning('This event has no entity type + id');
      return;
    }
    const type = String(detail.entity_type);
    const id = String(detail.entity_id);
    setDetail(null);
    setViewMode('entity');
    loadEntity(type, id);
  };

  useEffect(() => {
    if (viewMode === 'entity' && entityHistory == null && entityBrowseItems.length === 0) {
      loadEntityBrowse('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const entityHistoryItems = useMemo(() => {
    if (entityHistory != null) return normalizeEntityHistoryList(entityHistory);
    return entityBrowseItems;
  }, [entityHistory, entityBrowseItems]);

  const entityEventCount =
    entityHistory != null ? entityHistoryItems.length : entityBrowseTotal || entityHistoryItems.length;

  const selectedSession = useMemo(() => {
    const sessions = timeline?.sessions || [];
    if (!sessions.length) return null;
    return sessions.find((s) => s.session_id === selectedSessionId) || sessions[0];
  }, [timeline, selectedSessionId]);

  const activityActorLabel =
    timeline?.actor_name ||
    timeline?.actor_email ||
    actorId ||
    'All users';

  const actorOptions = useMemo(() => {
    const fromTimeline = [];
    if (timeline?.actor_email || timeline?.actor_id) {
      fromTimeline.push({
        value: timeline.actor_email || timeline.actor_id,
        label: timeline.actor_email || timeline.actor_name || timeline.actor_id,
      });
    }
    if (actorId.trim() && !fromTimeline.some((o) => o.value === actorId.trim())) {
      fromTimeline.push({ value: actorId.trim(), label: actorId.trim() });
    }
    return fromTimeline;
  }, [timeline, actorId]);

  const renderSeverity = (v) => (
    <span className={`audit-sev ${severityClass(v)}`}>{v || 'INFO'}</span>
  );

  const formatChangePreview = (row) => {
    const fields = row?.changed_fields;
    if (Array.isArray(fields) && fields.length) {
      return fields
        .slice(0, 2)
        .map((f) => `${f.field}: ${formatAuditValue(f.from)} → ${formatAuditValue(f.to)}`)
        .join('; ');
    }
    if (row?.previous || row?.changes) {
      return 'View details';
    }
    return '—';
  };

  const openDetail = (row) => {
    setActionDetailsOpen(false);
    setDetail(row);
  };

  const resolveSource = (row) => {
    if (row?.source) return String(row.source).toUpperCase();
    if (String(row?.module || '').toUpperCase() === 'ACTIVITY') return 'UI';
    return 'BE';
  };

  const formatModuleLabel = (row) => {
    const mod = row?.module || '—';
    const src = resolveSource(row);
    if (String(mod).toUpperCase() === 'ACTIVITY' || src === 'UI') {
      return mod === '—' ? 'User activity (UI)' : `${mod} (UI)`;
    }
    return mod;
  };

  const columns = [
    {
      title: 'When',
      dataIndex: 'created_at',
      width: 200,
      render: (v) => formatWhen(v),
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      width: 180,
      ellipsis: true,
      render: (v, row) => v || row.project_id || '—',
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      width: 100,
      render: (v) => renderSeverity(v),
    },
    {
      title: 'Module',
      key: 'module',
      width: 170,
      ellipsis: true,
      render: (_, row) => formatModuleLabel(row),
    },
    {
      title: 'Event',
      dataIndex: 'event_label',
      width: 200,
      ellipsis: true,
      render: (v, row) => v || row.event_type,
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      width: 260,
      ellipsis: true,
      render: (v, row) => v || row.display_title || '—',
    },
    {
      title: 'Change',
      key: 'change',
      width: 240,
      ellipsis: true,
      render: (_, row) => formatChangePreview(row),
    },
    {
      title: 'Entity',
      key: 'entity',
      width: 200,
      ellipsis: true,
      render: (_, row) => {
        const ent = resolveEntityDisplay(row);
        if (!ent.hasEntity) return '—';
        return (
          <span className="audit-entity-cell">
            <span className="audit-entity-cell__id" title={ent.primary}>
              {ent.primary}
            </span>
            {ent.secondary ? (
              <span className="audit-entity-cell__secondary">{ent.secondary}</span>
            ) : null}
            {ent.type ? <span className="audit-entity-cell__type">{ent.type}</span> : null}
          </span>
        );
      },
    },
    {
      title: 'Actor',
      dataIndex: 'actor_email',
      width: 220,
      ellipsis: true,
      render: (v, row) => v || row.actor_name || row.actor_id || '—',
    },
    {
      title: 'Details',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_, row) => (
        <button type="button" className="audit-view-link" onClick={() => openDetail(row)}>
          View
        </button>
      ),
    },
  ];

  const tableLoading = {
    spinning: loading,
    indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
  };

  return (
    <div className="modern-audit-trail-page">
      <div className="heat-profile-content-wrapper">
        <div className="heat-profile-card audit-trail-card">
          <div className="heat-profile-list-header">
            <div className="heat-profile-header-left">
              <div
                className="heat-profile-icon-wrap"
                style={{ backgroundColor: '#eff6ff', color: '#2563eb' }}
              >
                <History size={22} color="currentColor" strokeWidth={2} />
              </div>
              <div className="heat-profile-header-content">
                <div className="heat-profile-title-row">
                  <h3 className="heat-profile-list-title">Audit Trail</h3>
                </div>
                <p className="heat-profile-list-description">{description}</p>
              </div>
            </div>
            <div className="heat-profile-header-actions calibration-modern-actions audit-trail-header-actions">
              <CrceButton
                label="Refresh"
                variant="secondary"
                size="small"
                icon={RefreshCw}
                onClick={() => (viewMode === 'log' ? loadLog() : null)}
              />
              <CrceButton
                label="Export CSV"
                variant="tertiary"
                size="small"
                icon={Download}
                onClick={handleExport}
              />
            </div>
          </div>

          {stats && viewMode === 'log' && (
            <div className="audit-stats-row">
              <div className="audit-stat-card" style={{ '--stat-color': '#2563eb' }}>
                <div className="audit-stat-icon" style={{ backgroundColor: '#eff6ff', color: '#2563eb' }}>
                  <Activity size={20} strokeWidth={2} />
                </div>
                <div className="audit-stat-info">
                  <span className="audit-stat-count">{stats.total_events ?? 0}</span>
                  <span className="audit-stat-label">Total events</span>
                </div>
              </div>
              <div className="audit-stat-card" style={{ '--stat-color': '#00a88e' }}>
                <div className="audit-stat-icon" style={{ backgroundColor: '#ecfdf5', color: '#00a88e' }}>
                  <History size={20} strokeWidth={2} />
                </div>
                <div className="audit-stat-info">
                  <span className="audit-stat-count">{stats.events_today ?? 0}</span>
                  <span className="audit-stat-label">Today</span>
                </div>
              </div>
              <div className="audit-stat-card" style={{ '--stat-color': '#dc2626' }}>
                <div className="audit-stat-icon" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
                  <AlertTriangle size={20} strokeWidth={2} />
                </div>
                <div className="audit-stat-info">
                  <span className="audit-stat-count">{stats.failure_events ?? 0}</span>
                  <span className="audit-stat-label">Failures</span>
                </div>
              </div>
              <div className="audit-stat-card" style={{ '--stat-color': '#7c3aed' }}>
                <div className="audit-stat-icon" style={{ backgroundColor: '#f5f3ff', color: '#7c3aed' }}>
                  <Shield size={20} strokeWidth={2} />
                </div>
                <div className="audit-stat-info">
                  <span className="audit-stat-count">{stats.security_events ?? 0}</span>
                  <span className="audit-stat-label">Security</span>
                </div>
              </div>
            </div>
          )}

          <div className="audit-trail-body">
            <Tabs
              className="audit-trail-tabs"
              activeKey={viewMode}
              onChange={setViewMode}
            >
              <TabPane tab="Event log" key="log">
                <div className="audit-source-tabs" role="tablist" aria-label="Event source">
                  {[
                    { key: 'be', label: 'API changes' },
                    { key: 'ui', label: 'UI actions' },
                    { key: 'all', label: 'All events' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      role="tab"
                      aria-selected={eventSource === opt.key}
                      className={`audit-source-tabs__btn${
                        eventSource === opt.key ? ' is-active' : ''
                      }`}
                      onClick={() => {
                        setEventSource(opt.key);
                        setPage(1);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="audit-filters">
                  <div
                    className={`audit-filters-row audit-filters-row--primary${
                      enableProjectFilter ? ' audit-filters-row--with-project' : ''
                    }`}
                  >
                    {enableProjectFilter && (
                      <Select
                        showSearch
                        allowClear
                        loading={projectsLoading}
                        placeholder="Search project by name…"
                        className="audit-filter-select audit-filter-project"
                        value={projectFilter}
                        onChange={(val) => {
                          setProjectFilter(val);
                          setPage(1);
                        }}
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          String(option?.label ?? '')
                            .toLowerCase()
                            .includes(String(input).toLowerCase())
                        }
                        options={projectOptions.map((p) => ({
                          value: p.id,
                          label: p.projectName
                            ? p.projectId
                              ? `${p.projectName} (${p.projectId})`
                              : p.projectName
                            : `Project ${p.id}`,
                        }))}
                        dropdownClassName="modern-dropdown-popup"
                        getPopupContainer={popupToBody}
                      />
                    )}
                    <Select
                      allowClear
                      placeholder="Module"
                      className="audit-filter-select audit-filter-module"
                      value={module}
                      onChange={setModule}
                      dropdownClassName="modern-dropdown-popup"
                      getPopupContainer={popupToBody}
                    >
                      {MODULES.map((m) => (
                        <Option key={m} value={m}>
                          {m}
                        </Option>
                      ))}
                    </Select>
                    <Select
                      allowClear
                      placeholder="Severity"
                      className="audit-filter-select audit-filter-severity"
                      value={severity}
                      onChange={setSeverity}
                      dropdownClassName="modern-dropdown-popup"
                      getPopupContainer={popupToBody}
                    >
                      <Option value="INFO">INFO</Option>
                      <Option value="WARN">WARN</Option>
                      <Option value="ERR">ERR</Option>
                    </Select>
                    <Input
                      className="audit-search-input"
                      allowClear
                      placeholder="Search actor, entity, event, summary…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onPressEnter={applyFilters}
                      prefix={<Search size={15} className="audit-search-icon" />}
                    />
                  </div>
                  <div className="audit-filters-row audit-filters-row--secondary">
                    <RangePicker
                      className="audit-range-picker"
                      value={dateRange}
                      onChange={setDateRange}
                      dropdownClassName="modern-dropdown-popup"
                      getPopupContainer={popupToBody}
                      placement="bottomLeft"
                    />
                    <div className="audit-filters-actions">
                      <CrceButton
                        label="Apply"
                        variant="primary"
                        size="small"
                        onClick={applyFilters}
                      />
                      <CrceButton
                        label="Clear"
                        variant="secondary"
                        size="small"
                        onClick={clearFilters}
                      />
                    </div>
                  </div>
                </div>

                {error && (
                  <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
                )}

                <div className="spares-list-table-pager-wrap pipeline-table audit-table-pager-wrap">
                  <div className="audit-table-wrap heat-profile-table-wrap">
                    <Table
                      className="audit-table heat-profile-table"
                      rowKey="id"
                      size="middle"
                      loading={tableLoading}
                      columns={columns}
                      dataSource={items}
                      pagination={false}
                      scroll={{ x: 1840 }}
                    />
                  </div>
                  {total > 0 && (
                    <div className="spares-list-pager-outer">
                      <Pagination
                        className="heat-profile-pagination"
                        current={page}
                        pageSize={pageSize}
                        total={total}
                        showSizeChanger
                        pageSizeOptions={['10', '20', '50', '100']}
                        showTotal={(t, range) => `${range[0]}-${range[1]} of ${t} items`}
                        onChange={(p, size) => {
                          setPage(p);
                          if (size != null) setPageSize(size);
                        }}
                      />
                    </div>
                  )}
                </div>
              </TabPane>

              <TabPane tab="User activity" key="timeline">
                <div className="audit-d360-toolbar">
                  <Input
                    className="audit-d360-search"
                    allowClear
                    placeholder="Filter by actor name or email (leave empty for all users)..."
                    value={actorQuery}
                    onChange={(e) => setActorQuery(e.target.value)}
                    onPressEnter={() => {
                      const next = actorQuery.trim();
                      setActorId(next);
                      loadTimeline(next);
                    }}
                    prefix={<Search size={15} className="audit-search-icon" />}
                  />
                  <CrceButton
                    label="Load activity"
                    variant="primary"
                    size="small"
                    onClick={() => {
                      setActorId(actorQuery.trim());
                      loadTimeline(actorQuery.trim());
                    }}
                    disabled={timelineLoading}
                  />
                  <button
                    type="button"
                    className={`audit-d360-filters-btn${activityFiltersOpen ? ' is-open' : ''}`}
                    onClick={() => setActivityFiltersOpen((v) => !v)}
                  >
                    <Filter size={14} strokeWidth={2} />
                    Filters
                    <ChevronDown size={14} strokeWidth={2} />
                  </button>
                  <span className="audit-d360-count">
                    <span className="audit-d360-count__pill">
                      {timeline?.sessions?.length
                        ? `${timeline.sessions.length} session${timeline.sessions.length === 1 ? '' : 's'}`
                        : 'No sessions'}
                    </span>
                  </span>
                </div>

                {activityFiltersOpen && (
                  <div className="audit-d360-filters-panel">
                    <Select
                      className="audit-d360-user-select"
                      showSearch
                      allowClear
                      placeholder="Select or type a user email…"
                      value={actorId || undefined}
                      onChange={(val) => {
                        const next = val || '';
                        setActorId(next);
                        setActorQuery(next);
                        if (next) loadTimeline(next);
                        else {
                          setTimeline(null);
                          setSelectedSessionId(null);
                        }
                      }}
                      onSearch={(q) => setActorQuery(q)}
                      filterOption={false}
                      options={
                        actorQuery.trim() &&
                        !actorOptions.some((o) => o.value === actorQuery.trim())
                          ? [
                              ...actorOptions,
                              { value: actorQuery.trim(), label: actorQuery.trim() },
                            ]
                          : actorOptions
                      }
                      dropdownClassName="modern-dropdown-popup"
                      getPopupContainer={popupToBody}
                    />
                  </div>
                )}

                {timelineLoading && (
                  <div className="audit-inline-loading">
                    <Spinner variant="inline" size="medium" color="#00C9A7" />
                  </div>
                )}

                {!timelineLoading && timeline?.sessions?.length ? (
                  <div className="audit-activity-split">
                    <aside className="audit-activity-sessions">
                      <div className="audit-activity-sessions__label">
                        Sessions — {activityActorLabel}
                      </div>
                      <div className="audit-activity-sessions__list">
                        {timeline.sessions.map((session) => {
                          const active = session.session_id === selectedSession?.session_id;
                          const count = session.event_count ?? session.events?.length ?? 0;
                          return (
                            <button
                              key={session.session_id}
                              type="button"
                              className={`audit-session-tile${active ? ' is-active' : ''}`}
                              onClick={() => setSelectedSessionId(session.session_id)}
                            >
                              <div className="audit-session-tile__when">
                                {formatSessionRange(session)}
                              </div>
                              <div className="audit-session-tile__id">
                                Session {shortSessionId(session.session_id)}
                              </div>
                              <span className="audit-session-tile__count">
                                {count} event{count === 1 ? '' : 's'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </aside>

                    <section className="audit-activity-detail">
                      {selectedSession ? (
                        <>
                          <header className="audit-activity-detail__head">
                            <div className="audit-activity-detail__avatar" aria-hidden>
                              {(activityActorLabel || '?').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <h4 className="audit-activity-detail__title">
                                {activityActorLabel}
                              </h4>
                              <p className="audit-activity-detail__sub">
                                Session {shortSessionId(selectedSession.session_id)} ·{' '}
                                {formatSessionRange(selectedSession)}
                              </p>
                            </div>
                          </header>
                          <ol className="audit-timeline">
                            {(selectedSession.events || []).map((ev) => (
                              <li
                                key={ev.id || `${ev.created_at}-${ev.event_type}`}
                                className="audit-timeline__item"
                              >
                                <div className="audit-timeline__marker" aria-hidden />
                                <div className="audit-timeline__body">
                                  <div className="audit-timeline__row">
                                    <time className="audit-timeline__time">
                                      {formatClock(ev.created_at)}
                                    </time>
                                    <span className="audit-timeline__summary">
                                      {eventHeadline(ev)}
                                    </span>
                                  </div>
                                  <div className="audit-timeline__meta">
                                    {ev.module || 'Activity'}
                                    {ev.project_name ? ` · ${ev.project_name}` : ' · System'}
                                  </div>
                                  <span className="audit-timeline__type">{eventTypeLabel(ev)}</span>
                                </div>
                              </li>
                            ))}
                          </ol>
                        </>
                      ) : (
                        <p className="audit-empty-hint">Select a session to view its timeline.</p>
                      )}
                    </section>
                  </div>
                ) : (
                  !timelineLoading && (
                    <div className="audit-empty-state">
                      <Activity size={28} strokeWidth={1.5} />
                      <p>Search or load a user to see their session activity.</p>
                    </div>
                  )
                )}
              </TabPane>

              <TabPane tab="Entity history" key="entity">
                <div className="audit-d360-toolbar">
                  <Input
                    className="audit-d360-search"
                    allowClear
                    placeholder="Report name, PQR number, weld number, file name..."
                    value={entitySearch}
                    onChange={(e) => setEntitySearch(e.target.value)}
                    onPressEnter={() => {
                      setEntityType('');
                      setEntityId('');
                      loadEntityBrowse(entitySearch);
                    }}
                    prefix={<Search size={15} className="audit-search-icon" />}
                  />
                  <button
                    type="button"
                    className={`audit-d360-filters-btn${entityFiltersOpen ? ' is-open' : ''}`}
                    onClick={() => setEntityFiltersOpen((v) => !v)}
                  >
                    <Filter size={14} strokeWidth={2} />
                    Filters
                    <ChevronDown size={14} strokeWidth={2} />
                  </button>
                  <span className="audit-d360-count">
                    <span className="audit-d360-count__pill">
                      {entityEventCount} history event{entityEventCount === 1 ? '' : 's'}
                    </span>
                  </span>
                </div>

                {entityFiltersOpen && (
                  <div className="audit-d360-filters-panel audit-d360-filters-panel--entity">
                    <Input
                      className="audit-filter-input"
                      placeholder="Entity type (e.g. USER)"
                      value={entityType}
                      onChange={(e) => setEntityType(e.target.value)}
                    />
                    <Input
                      className="audit-filter-input"
                      placeholder="Entity id"
                      value={entityId}
                      onChange={(e) => setEntityId(e.target.value)}
                      onPressEnter={() => loadEntity()}
                    />
                    <div className="audit-d360-filters-panel__actions">
                      <CrceButton
                        label="Load entity"
                        variant="primary"
                        size="small"
                        onClick={() => loadEntity()}
                        disabled={entityLoading}
                      />
                      <CrceButton
                        label="Search all"
                        variant="secondary"
                        size="small"
                        onClick={() => {
                          setEntityType('');
                          setEntityId('');
                          loadEntityBrowse(entitySearch);
                        }}
                        disabled={entityLoading}
                      />
                    </div>
                  </div>
                )}

                <p className="audit-d360-hint">
                  Recent {entityEventCount} events · newest first · search above to narrow.
                </p>

                {entityLoading && (
                  <div className="audit-inline-loading">
                    <Spinner variant="inline" size="medium" color="#00C9A7" />
                  </div>
                )}

                {!entityLoading && entityHistoryItems.length > 0 ? (
                  <div className="audit-entity-cards">
                    {entityHistoryItems.map((row) => (
                      <article
                        key={row.id || `${row.created_at}-${row.event_type}-${row.entity_id}`}
                        className="audit-entity-card"
                      >
                        <div className="audit-entity-card__top">
                          <span className="audit-entity-card__when">{formatWhen(row.created_at)}</span>
                          <span className={`audit-sev ${severityClass(row.severity)}`}>
                            {row.severity || 'INFO'}
                          </span>
                        </div>
                        <h4 className="audit-entity-card__title">{eventHeadline(row)}</h4>
                        <p className="audit-entity-card__meta">{eventMetaLine(row)}</p>
                        <div className="audit-entity-card__footer">
                          <button
                            type="button"
                            className="audit-entity-card__view"
                            onClick={() => openDetail(row)}
                          >
                            <Eye size={14} strokeWidth={2} />
                            View details
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  !entityLoading && (
                    <div className="audit-empty-state">
                      <History size={28} strokeWidth={1.5} />
                      <p>
                        {entityType && entityId
                          ? `No history for ${entityType} / ${entityId}.`
                          : 'No history events match this search.'}
                      </p>
                    </div>
                  )
                )}
              </TabPane>
            </Tabs>
          </div>
        </div>
      </div>

      <Modal
        visible={!!detail}
        title={detail?.event_label || detail?.event_type || 'Event detail'}
        onCancel={() => {
          setDetail(null);
          setActionDetailsOpen(false);
        }}
        footer={null}
        width={720}
        className="audit-detail-modal"
        wrapClassName="audit-detail-modal-wrap"
        destroyOnClose
      >
        {detail && (
          <div className="audit-detail-body">
            <p>
              <strong>When:</strong> {formatWhen(detail.created_at)}
            </p>
            <p>
              <strong>Source:</strong> {resolveSource(detail)}
            </p>
            <p>
              <strong>Project:</strong>{' '}
              {detail.project_name || detail.project_id || '—'}
            </p>
            <p>
              <strong>Severity:</strong> {detail.severity || 'INFO'}
            </p>
            <p>
              <strong>Module:</strong> {detail.module || '—'}
            </p>
            <p>
              <strong>Summary:</strong> {detail.summary || '—'}
            </p>
            <p>
              <strong>Actor:</strong>{' '}
              {detail.actor_name || detail.actor_email || detail.actor_id || '—'}
            </p>
            <p className="audit-detail-entity-row">
              <strong>Entity:</strong>{' '}
              <span>
                {(() => {
                  const ent = resolveEntityDisplay(detail);
                  return (
                    <>
                      {detail.entity_type || '—'}
                      {detail.entity_id != null && detail.entity_id !== ''
                        ? ` / ${detail.entity_id}`
                        : ''}
                      {ent.primary && ent.primary !== '—' && ent.primary !== String(detail.entity_id)
                        ? ` (${ent.primary})`
                        : ''}
                      {ent.secondary ? ` · ${ent.secondary}` : ''}
                    </>
                  );
                })()}
              </span>
              {detail.entity_type && detail.entity_id != null && detail.entity_id !== '' && (
                <button
                  type="button"
                  className="audit-view-link audit-entity-history-link"
                  onClick={openEntityHistoryFromDetail}
                >
                  View entity history
                </button>
              )}
            </p>
            <p>
              <strong>Session:</strong> {detail.session_id || '—'}
            </p>

            {detail.entity_context && typeof detail.entity_context === 'object' && (
              <div className="audit-detail-change-block">
                <strong>Entity details</strong>
                <table className="audit-change-table audit-entity-context-table">
                  <tbody>
                    {Object.entries(detail.entity_context)
                      .filter(([, v]) => v != null && v !== '')
                      .map(([key, value]) => (
                        <tr key={key}>
                          <td className="audit-change-table__field">{humanizeContextKey(key)}</td>
                          <td colSpan={2}>
                            <code>{formatAuditValue(value)}</code>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {(detail.previous || detail.changes || (detail.changed_fields || []).length > 0) && (
              <div className="audit-detail-change-block">
                <strong>What changed</strong>
                {Array.isArray(detail.changed_fields) && detail.changed_fields.length > 0 ? (
                  <table className="audit-change-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Previous</th>
                        <th>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.changed_fields.map((f) => (
                        <tr key={f.field}>
                          <td className="audit-change-table__field">{f.field}</td>
                          <td>
                            <code>{formatAuditValue(f.from)}</code>
                          </td>
                          <td>
                            <code>{formatAuditValue(f.to)}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="audit-change-panels">
                    <div>
                      <div className="audit-change-panel-label">Previous</div>
                      <pre>{JSON.stringify(detail.previous ?? null, null, 2)}</pre>
                    </div>
                    <div>
                      <div className="audit-change-panel-label">New / changes</div>
                      <pre>{JSON.stringify(detail.changes ?? null, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="audit-detail-actions-block">
              <button
                type="button"
                className="audit-action-details-toggle"
                onClick={() => setActionDetailsOpen((o) => !o)}
                aria-expanded={actionDetailsOpen}
              >
                {actionDetailsOpen ? (
                  <ChevronDown size={16} strokeWidth={2} />
                ) : (
                  <ChevronRight size={16} strokeWidth={2} />
                )}
                Full action details
              </button>
              {actionDetailsOpen &&
                (detail.action_details != null ? (
                  <pre>{JSON.stringify(detail.action_details, null, 2)}</pre>
                ) : (
                  <p className="audit-empty-hint">No action details recorded for this event.</p>
                ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
